import { isNull, schPairedSalesCreateRequest } from '@aloft/toolkit-shared';

import { useRef } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { usePropertyCoordinates } from '../../hooks/usePropertyCoordinates.ts';
import { useToaster } from '../../hooks/useToaster/useToaster.tsx';
import { AdjustPairedSalesStandaloneModule } from '../../modules/adjustPairedSalesStandalone/AdjustPairedSalesStandaloneModule.tsx';
import {
  AdjustPairedSalesStandaloneModuleProps,
  StudyOutput,
} from '../../modules/adjustPairedSalesStandalone/types.ts';
import { isPairedSalesCustomAnalysisFeature } from '../../modules/adjustPairedSalesStandalone/utils/isPairedSalesCustomAnalysisFeature.ts';
import { formatReferenceViewID } from '../../modules/referenceView/referenceViewID/formatReferenceViewID.ts';
import { AddStudy, schCreateStudy } from '../../modules/studies/hooks/useAddStudyForm.ts';
import { getFallbackCenterCoordinates } from '../../utils/getFallbackCenterCoordinates.ts';
import { findDisplayFormatForTerm } from '../adjust/schema/findDisplayFormatForTerm.ts';
import { gridGeneric } from '../adjust/schema/gridGeneric.ts';
import { useMetadataByID } from '../hooks/useMetadataByID.ts';
import { useStudiesState } from '../hooks/useStudiesState.ts';
import { useTkv2OrderID } from '../hooks/useTkv2OrderID.ts';
import { getInitialStudy } from '../pairedSales/getInitialStudy.ts';

const studiesRelativePath = '../../studies';

export const PairedSalesStandaloneRouteElement = () => {
  const makeToast = useToaster();
  const hasClearedOldMapState = useRef(false);

  const { createStudyReq, updateStudyReq, studies, selectedStudies, setSelectedStudies } =
    useStudiesState();

  const navigate = useNavigate();

  const orderID = useTkv2OrderID();
  const subjectMetadataByID = useMetadataByID()[formatReferenceViewID({ type: 'subject' })];
  const subjectPinCoordinates = usePropertyCoordinates(
    subjectMetadataByID ?? { address: undefined, listingID: undefined },
  );

  const modalInput = useLocation().state as AddStudy | null;
  const { studyID } = useParams();

  if (!!modalInput && !schCreateStudy.safeParse(modalInput).success) {
    return <Navigate to={studiesRelativePath} replace state={{}} />;
  }

  if (!hasClearedOldMapState.current) {
    // clear out any old map state hanging around
    window.localStorage.removeItem('paired-sales-map');
    hasClearedOldMapState.current = true;
  }

  const initialStudy = getInitialStudy(modalInput, studyID, studies.data);
  const { analysisFeature } = initialStudy.input;

  const createStudy = async (study: StudyOutput) => {
    try {
      const studyRequest = {
        body: study.body,
        input: study.input,
      };

      schPairedSalesCreateRequest.parse(studyRequest);

      const res = await createStudyReq.mutateAsync({ body: studyRequest });

      if (!selectedStudies.includes(res.id as string)) {
        setSelectedStudies([...selectedStudies, res.id]);
      }

      return res;
    } catch (err) {
      console.warn(err);
      makeToast({
        type: 'error',
        id: 'create-study-error',
        title: 'Error creating study',
      });
    }

    return undefined;
  };

  const updateStudy = async (study: StudyOutput) => {
    if (!studyID || !orderID) {
      throw new Error('Cannot update study without studyID and orderID');
    }
    try {
      const studyRequest = {
        body: study.body,
        input: study.input,
      };

      const { body } = schPairedSalesCreateRequest.parse(studyRequest);

      const res = await updateStudyReq.mutateAsync({
        pathParams: { studyID },

        body: { body },
      });

      if (!selectedStudies.includes(res.id)) {
        setSelectedStudies([...selectedStudies, res.id]);
      }

      return res;
    } catch (err) {
      console.warn(err);
      makeToast({
        type: 'error',
        id: 'update-study-error',
        title: 'Error updating study',
      });
    }

    return undefined;
  };

  const props: AdjustPairedSalesStandaloneModuleProps = {
    initialStudy,
    isEditing: isNull(modalInput),
    mapProps: {
      subject: subjectPinCoordinates.coordinates,
      center: subjectPinCoordinates.coordinates ?? getFallbackCenterCoordinates(),
      zoom: 14,
    },
    getDisplayValue: isPairedSalesCustomAnalysisFeature(analysisFeature)
      ? () => ''
      : findDisplayFormatForTerm(gridGeneric, analysisFeature),
    onCancel: () => navigate(studiesRelativePath),
    onSave: async (study) => {
      if (studyID) await updateStudy(study);
      else await createStudy(study);
    },
    onFinish: () => navigate(studiesRelativePath),
  };

  const loading = subjectPinCoordinates.isLoading || studies.isLoading;
  if (loading) return null;

  return <AdjustPairedSalesStandaloneModule {...props} />;
};
